import React, { useState } from 'react'
import { injectIntl } from 'react-intl'
import Spinner from '../../util/spinner-shim'
import PropTypes from 'prop-types'
import { Helmet } from 'react-helmet-async'
import moment from 'moment'
import fileD from 'js-file-download'

import Highcharts from '../../highchartsInit'
import { Chart as HighchartsReact } from '@highcharts/react'


import statusCalculate from 'altrac-application-status'
import AppDispatcher from '../../dispatchers/AppDispatcher'
import AppStore from '../../stores/AppStore'
import NavigationBar from '../partials/NavigationBar'
import Footer from '../partials/Footer'
import Headline from '../partials/ConfigurePageComponents/Headline'
import DateRangePicker from '../partials/ConfigurePageComponents/DateRangePicker'
import { useDateRangePicker } from '../partials/ConfigurePageComponents/useDateRangePicker'
import pageStyles from '../../../css/devicePage.module.css'
import { isNumber } from '../partials/Inputs/Inputs'
import { useParams, useNavigate } from 'react-router-dom'
import withAppStore from '../../util/withAppStore'

const lineColors = {
  cyan: '#008B8B',
  darkBlue: '#284047',
  darkBlueO: 'rgb(40,64,71,0.2)',
  fuchsia: '#CE23A9',
  gray: '#666',
  green: '#23CE6B',
  lightBlue: '#50B0FF',
  orange: '#FFBA08',
  purple: '#707EFE',
  purpleO: 'rgb(112,126,254,0.2)',
  red: '#D00000',
  yellow: '#FFEE58',
}

const { red, orange, gray, green, lightBlue, darkBlue, darkBlueO, yellow, purple, purpleO, fuchsia } = lineColors

const lineColorsArr = [red, orange, green, lightBlue, darkBlue, yellow, purple, fuchsia, red, orange, green, lightBlue, darkBlue, yellow, purple, fuchsia]
const lineDashArr = ['normal', 'normal', 'normal', 'normal', 'normal', 'normal', 'normal', 'normal', 'ShortDash', 'ShortDash', 'ShortDash', 'ShortDash', 'ShortDash', 'ShortDash', 'ShortDash', 'ShortDash']

const getMin = (arr, sel, start) => arr.reduce((min, p) => ((p[sel] < min) ? p[sel] : min), isNumber(arr[0][sel]) ? Number(arr[0][sel]) : start)
const getMax = (arr, sel, start) => arr.reduce((max, p) => ((p[sel] > max) ? p[sel] : max), isNumber(arr[0][sel]) ? Number(arr[0][sel]) : start)

const localTZ = (iso) => (new Date(iso).getTime()) - (new Date().getTimezoneOffset() * 60 * 1000)

const AdvancedComponent = (props) => {
  const {
    formatMessage,
    labels: labelTexts = [],
    series = [],
    tempConv,
    unit,
    title,
    yProperty,
    domain = {},
    goalMax,
    goalMin,
    setPoints,
    name,
  } = props

  const GOAL_MAX = formatMessage({
    id: 'general.goalMax',
    defaultMessage: 'Goal Max',
    description: 'max label',
  })

  const GOAL_MIN = formatMessage({
    id: 'general.goalMin',
    defaultMessage: 'Goal Min',
    description: 'min label',
  })

  const AVERAGE = formatMessage({
    id: 'general.average',
    defaultMessage: 'Average',
    description: 'avg label',
  })

  const SETPOINT = `${formatMessage({
    id: 'device.moisture.setpoint.label',
    defaultMessage: 'Set Point',
    description: 'set point label',
  })}`

  const defaultIsVisible = {}
  for (let i = 0; i < labelTexts.length; i++) {
    defaultIsVisible[labelTexts[i]] = true
  }

  const [isAverage, setIsAverage] = useState(true)

  const toggleAverage = () => {
    const average = !isAverage
    setIsAverage(average)
  }

  const [chart, setChart] = useState(true)

  const catchChart = (_chart) => {
    setChart(_chart)
  }

  // don't display chart if series contains no data
  if (!series ||
    !series.length ||
    !series[0][yProperty].length
  ) return ''

  let dataConv = (x) => x // default to no data conversion

  if (yProperty === 'temperature' && tempConv === 'f') {
    dataConv = (x) => isNumber(x) && Math.round(32 + (x * 1.8))
  }

  let {
    y: [chartMin, chartMax],
  } = domain
  if (goalMax > chartMax) chartMax = goalMax + 1
  if (goalMin < chartMin) chartMin = goalMin - 1

  const hcData = []
  const convert = (x) => (x ? 100 : 0)

  if (yProperty !== 'temperature') {
    const hcPressure1 = []
    const hcPressure2 = []
    series.map((x) => {
      hcPressure1.push(
        [localTZ(x.dateBegin), convert(x.p1)],
        [localTZ(x.dateEnd), convert(x.p1)]
      )
      hcPressure2.push(
        [localTZ(x.dateBegin), convert(x.p2)],
        [localTZ(x.dateEnd), convert(x.p2)]
      )
      return 1
    })

    hcData.push({
      name: 'Pressure 1',
      data: hcPressure1,
      type: 'area',
      color: darkBlueO,
      includeInDataExport: false,
    }, {
      name: 'Pressure 2',
      data: hcPressure2,
      type: 'area',
      color: purpleO,
      includeInDataExport: false,
    })
  }

  let good = false
  let key = ''
  switch (yProperty) {
    case 'moisture':
      key = 'avgMoisture'
      break
    case 'salinity':
      key = 'avgSalinity'
      break
    case 'temperature':
      key = 'avgTemperature'
      break

    default:
      break
  }
  const goalMinArr = []
  const goalMaxArr = []
  const data = series.map((item) => {
    const value = dataConv(item[key])
    if (value) good = true
    const arr = [
      localTZ(item.dateEnd),
      value,
    ]
    if (goalMax) goalMaxArr.push([localTZ(item.dateEnd), goalMax])
    if (goalMin) goalMinArr.push([localTZ(item.dateEnd), goalMin])
    return arr
  })
  if (goalMax) {
    hcData.push({
      name: GOAL_MAX,
      data: goalMaxArr,
      type: 'spline',
      color: lightBlue,
      marker: {
        enabled: false,
      },
      visible: isAverage,
    })
  }
  if (goalMin) {
    hcData.push({
      name: GOAL_MIN,
      data: goalMinArr,
      type: 'spline',
      color: orange,
      marker: {
        enabled: false,
      },
      visible: isAverage,
    })
  }

  if (setPoints && setPoints.length > 0) {
    setPoints.forEach((point, i) => {
      if (!isNumber(point) || point <= 0 || point > 100) {
        return
      }
      const pointValue = Number(point)
      if (pointValue > chartMax) chartMax = pointValue + 1
      if (pointValue < chartMin) chartMin = pointValue - 1
      const dataArr = []
      series.forEach((item) => {
        dataArr.push([
          localTZ(item.dateEnd),
          Number(pointValue),
        ])
      })
      hcData.push({
        name: `${SETPOINT} ${i + 1}`,
        data: dataArr,
        type: 'spline',
        color: gray,
        dashStyle: 'ShortDash',
        marker: {
          enabled: false,
        },
        visible: isAverage,
      })
    })
  }

  hcData.push({
    name: AVERAGE,
    data,
    type: 'spline',
    color: red,
    marker: {
      enabled: false,
    },
    lineWidth: 4,
    visible: isAverage,
  })

  for (let i = 0; i < labelTexts.length; i++) {
    const data1 = series.map((item) => {
      const value = dataConv(item[yProperty][i])
      if (value) good = true
      const arr = [
        localTZ(item.dateEnd),
        value,
      ]
      return arr
    })
    const obj = {
      name: labelTexts[i],
      data: data1,
      type: 'spline',
      color: lineColorsArr[i],
      dashStyle: lineDashArr[i],
      marker: {
        enabled: false,
      },
      lineWidth: 2,
      visible: !isAverage,
    }
    hcData.push(obj)
  }

  if (good === false) return ''

   const hcOptions = {
    accessibility: {
      enabled: false,
    },
    title: { text: '' },
    chart: {
      height: '100%',
      zoomType: 'x',
      panning: {
        enabled: true,
      },
      resetZoomButton: {
        theme: {
          display: 'none',
        },
      },
    },
    xAxis: {
      type: 'datetime',
      dateTimeLabelFormats: {
        day: '%e %b',
        month: '%b %e',
        year: '%b',
      },
      title: {
        enabled: false,
      },
      // tickInterval: 1000 * 60 * 60 * 24,
      tickPixelInterval: 75,
    },
    yAxis: {
      min: Math.floor(chartMin),
      max: Math.ceil(chartMax),
      title: {
        enabled: false,
      },
      labels: {
        format: yProperty === 'temperature' ? '{value}' : '{value}%',
      },
      allowDecimals: false,
      endOnTick: false,
      startOnTick: false,
    },
    plotOptions: {
      series: {
        states: {
          inactive: {
            enabled: false,
          },
        },
        events: {
          legendItemClick () {
          },
        },
      },
    },
    tooltip: {
      formatter () {
        if (yProperty === 'moisture' && this.series.name === 'Average' && goalMax && goalMin) {
          return `<b>${moment.utc(this.x).format('LLL')}</b><br><span>Moisture Avg: ${this.y}</span><br><span>Moisture Score: ${(Math.round(((this.y - goalMin) / (goalMax - goalMin)) * 100))}</span>`
        }
        return `<b>${moment.utc(this.x).format('LLL')}</b><br><span>${this.series.name}: ${this.y}</span>`
      },
    },
    legend: {
      enabled: true,
      maxHeight: 100,
      padding: 3,
      itemMarginTop: 5,
      itemMarginBottom: 5,
    },
    series: hcData,
    credits: {
      enabled: false,
    },
    exporting: {
      enabled: false,
    },
  }

  return (
    <div>
      <div className={pageStyles.chartContainerFull}>
        <div className={pageStyles.chartTitleContainer}>
          <h4 className={pageStyles.chartTitle}>{title}</h4>
          <div style={{ position: 'relative', paddingTop: '7px' }}>
            <button
              type='button'
              onClick={() => {
                fileD(chart.getCSV().replace(/""/g, '"""'), `${name}_${title}_${moment(series[0].dateBegin).format('YYYY-MM-DD')}_${moment(series[series.length - 1].dateEnd).format('YYYY-MM-DD')}.csv`)
              }}
              className={pageStyles.chartTitleSetting}
              style={{
                cursor: 'pointer',
                '&:focus': { outline: '#CCC' },
                '&:hover': { background: '#CCC' },
              }}
            >
              <i className='fa fa-download' />
            </button>
            &nbsp;
            &nbsp;
            &nbsp;
            &nbsp;
            &nbsp;
            <button
              type='button'
              onClick={toggleAverage}
              className={pageStyles.chartTitleSetting}
              style={{
                cursor: 'pointer',
                '&:focus': { outline: '#CCC' },
                '&:hover': { background: '#CCC' },
              }}
            >
              <i className={`fa ${isAverage ? 'fa-plus' : 'fa-minus'}`} />
            </button>
          </div>
        </div>
        <div>
          <HighchartsReact
            highcharts={Highcharts}
            options={hcOptions}
            callback={catchChart}
          />
        </div>
      </div>
    </div>
  )
}

const AdvancedComponents = (props) => {
  const {
    formatMessage,
    height,
    series,
    settings = {},
    tempConv,
    width,
    name,
  } = props

  const MOISTURE_SENSOR = `${formatMessage({
    id: 'device.moisture.moistureSensor',
    defaultMessage: 'Soil Moisture',
    description: 'moisture sensor label',
  })}`

  const SALINITY_SENSORS = `${formatMessage({
    id: 'device.moisture.salinitySensors',
    defaultMessage: 'Salinity',
    description: 'salinity sensors label',
  })}`

  const TEMPERATURE_SENSORS = `${formatMessage({
    id: 'device.moisture.temperatureSensors',
    defaultMessage: 'Temperature',
    description: 'temperature sensors label',
  })}`

  if (series && series.length > 0) {
    const lines = series[0].moisture.length
    const labels = []
    const {
      moistureCombined: {
        goalMax = undefined,
        goalMin = undefined,
        setPoints = [],
        setPointsAreVisible = false,
        depthStep = 4, depthUnit = '"', depthZero = 5,
      } = {},
    } = settings || {}

    // make a label for each depth line
    for (let i = 0; i < lines; i++) {
      labels[i] = `${depthZero + (depthStep * i)}${depthUnit}`
    }

    const adjustMaximum = (x) => Math.ceil(x / 2) * 2 + 2
    const adjustMinimum = (x) => Math.floor(x / 2) * 2 - 2
    const adjustTemperature = (x) => (tempConv === 'f' ? (x * 1.8 + 32) : x)

    const moistureMax = adjustMaximum(getMax(series, 'maxMoisture', 0))
    const moistureMin = adjustMinimum(getMin(series, 'minMoisture', 100))
    const salinityMax = adjustMaximum(getMax(series, 'maxSalinity', 0))
    const salinityMin = adjustMinimum(getMin(series, 'minSalinity', 100))
    const temperatureMax = adjustTemperature(adjustMaximum(getMax(series, 'maxTemperature'), -20))
    const temperatureMin = adjustTemperature(adjustMinimum(getMin(series, 'minTemperature'), 50))

    return (
      <div>
        <AdvancedComponent
          formatMessage={formatMessage}
          height={height}
          labels={labels}
          series={series}
          title={MOISTURE_SENSOR}
          width={width}
          unit='%'
          yProperty='moisture'
          domain={{ y: [moistureMin, moistureMax] }}
          name={name}
          goalMax={goalMax}
          goalMin={goalMin}
          setPoints={setPointsAreVisible && setPoints.length > 0 ? setPoints : undefined}
        />
        <AdvancedComponent
          formatMessage={formatMessage}
          height={height}
          labels={labels}
          series={series}
          title={SALINITY_SENSORS}
          width={width}
          unit=''
          yProperty='salinity'
          domain={{ y: [salinityMin, salinityMax] }}
          name={name}
        />
        <AdvancedComponent
          formatMessage={formatMessage}
          height={height}
          labels={labels}
          series={series}
          tempConv={tempConv}
          title={TEMPERATURE_SENSORS}
          unit='°'
          width={width}
          yProperty='temperature'
          domain={{ y: [temperatureMin, temperatureMax] }}
          name={name}
        />
      </div>
    )
  }

  return ''
}

function MoistureSensorPage (props) {
  const { customerID, deviceID } = useParams()
  const navigate = useNavigate()

  // Default to 30 days (must be >= 1 to avoid dateBegin > dateEnd)
  const initialViewDays = React.useMemo(() => {
    const value = Number.isFinite(props.viewDays) ? props.viewDays : 30
    return Math.max(1, value)
  }, [props.viewDays])

  // Memoize initial dates to prevent unnecessary re-renders
  const initialDateBegin = React.useMemo(
    () => moment().startOf('day').subtract(initialViewDays, 'days'),
    [initialViewDays]
  )
  const initialDateEnd = React.useMemo(
    () => moment().endOf('day'),
    []
  )

  // Use the shared date range picker hook
  const dateRangePicker = useDateRangePicker(initialDateBegin, initialDateEnd)

  const [state, setState] = React.useState({
    viewDays: initialViewDays,
    showAdvancedComponents: true,
  })

  // Sync date range picker state with local state when needed
  const { dateBegin, dateEnd, dateType, dayPickerIsVisible, draftRange } = dateRangePicker.state

  const resizeTimeout = React.useRef(false)

  const getDevice = React.useCallback(() => {
    AppDispatcher.dispatch({ action: 'get-device', deviceID })
  }, [deviceID])

  const getStats = React.useCallback((timeBeg, timeEnd) => {
    AppDispatcher.dispatch({
      action: 'get-moisture-sensor-stats',
      deviceID,
      timeBeg: timeBeg.toISOString(),
      timeEnd: timeEnd.toISOString(),
    })
  }, [deviceID])

  const handleResize = React.useCallback(() => {
    clearTimeout(resizeTimeout.current)
    resizeTimeout.current = setTimeout(() => {
      // Force re-render
      setState(prev => ({ ...prev }))
    }, 500)
  }, [])

  const back = React.useCallback(() => {
    const { data: { history: { list = [] } = {} } = {} } = props
    navigate(list.length > 0 ? list : '/home')
  }, [props.data.history.list, navigate])

  const goto = React.useCallback((path) => {
    navigate(path)
  }, [navigate])

  const toMoistureCombinedSettings = React.useCallback(() => {
    goto(`/${customerID}/moistureSensor/${deviceID}/settings/moistureCombined`)
  }, [customerID, deviceID, goto])

  const toAdvancedSettings = React.useCallback(() => {
    goto(`/${customerID}/devices/${deviceID}/settings`)
  }, [customerID, deviceID, goto])

  // Use handlers from the date range picker hook
  const { closeDayPicker, editDateRange, handleDayClick, resetToLast31Days, applyDraftRange } = dateRangePicker

  // Wrapper to trigger getStats after date changes
  const handleDayClickWithStats = React.useCallback((range) => {
    handleDayClick(range)
    // getStats will be triggered by useEffect when dates change
  }, [handleDayClick])

  // Trigger getStats when dates change
  React.useEffect(() => {
    if (!dayPickerIsVisible && dateBegin && dateEnd) {
      getStats(dateBegin, dateEnd)
    }
  }, [dateBegin, dateEnd, dayPickerIsVisible, getStats])

  const resetDateRange = React.useCallback(() => {
    const newDateBegin = moment()
      .startOf('day')
      .subtract(state.viewDays, 'days')
      .add(1, 'days')
    const newDateEnd = moment()
      .endOf('day')

    dateRangePicker.setDateRange(newDateBegin, newDateEnd)
  }, [state.viewDays, dateRangePicker])

  React.useEffect(() => {
    getDevice()
    window.addEventListener('resize', handleResize)

    return () => {
      AppDispatcher.dispatch({
        action: 'unload-metric',
      })
      window.removeEventListener('resize', handleResize)
    }
  }, [getDevice, handleResize])

  React.useEffect(() => {
    const { data: { lookupReady, lookup } = {} } = props || {}
    const device = lookup[deviceID]

    if (lookupReady && device) {
      const {
        physical: {
          moistureSensorSettings: {
            moistureCombined: {
              viewDays: newViewDays = 30,
            } = {},
          } = {},
        } = {},
      } = device || {}

      const { viewDays } = state

      if (viewDays !== newViewDays) {
        setState(prev => ({ ...prev, viewDays: newViewDays }))
      }
    }
  }, [props.data.lookupReady, props.data.lookup, deviceID, state.viewDays, resetDateRange])

  // Reset date range when viewDays changes (prevents stale/invalid ranges)
  const prevViewDaysRef = React.useRef(state.viewDays)
  React.useEffect(() => {
    const { viewDays } = state
    if (viewDays && viewDays !== prevViewDaysRef.current) {
      prevViewDaysRef.current = viewDays
      resetDateRange()
    }
  }, [state.viewDays, resetDateRange])

  const render = () => {
    const {
      auth: { logout },
      data,
      pages: { MoistureSensorPage: title },
      route: { path: page },
      intl: { formatMessage },
    } = props

    const {
      showAdvancedComponents,
    } = state

    // Source of truth is AppStore.data (provided via withAppStore)
    const { statsReady } = data

    const {
      dateBegin,
      dateEnd,
      dateType,
      dayPickerIsVisible,
    } = dateRangePicker.state

    let pageContents = (
      <div style={{ height: '400px', position: 'relative' }}>
        <Spinner
          style={{
            position: 'absolute',
            top: '50%',
            right: '50%',
            transform: 'translate(50%, -50%)',
          }}
        />
      </div>
    )

    let name
    let device
    let headline = ''

    if (data.lookupReady && data.lookup) {
      device = data.lookup[deviceID]

      const {
        physical: {
          deviceNumber,
        } = {},
      } = device || {}

      name = deviceNumber

      const {
        deviceHeadlineDanger,
      } = statusCalculate(device)

      headline = (
        <Headline
          info={deviceHeadlineDanger}
          prepend={formatMessage({
            id: 'headline.error',
            defaultMessage: 'ERROR: ',
            description: '',
          })}
        />
      )
    }

    // Render content when device is loaded, stats are loaded, and stats is a valid array
    if (
      data.lookupReady && data.lookup &&
      statsReady === 'loaded' &&
      Array.isArray(data.stats)
    ) {
      const defaultMoistureSensorSettings = {
        moistureCombined: {
          goalMax: undefined,
          goalMin: undefined,
          goalsAreVisible: true,
          setPoints: [],
          setPointsAreVisible: false,
          viewDays: 30,
        },
        depthUnit: '"',
        depthZero: 5,
        depthStep: 4,
      }

      const {
        physical: {
          moistureSensorSettings = defaultMoistureSensorSettings,
        } = {},
      } = device || {}

      pageContents = (
        <div style={{ float: 'left' }}>
          {headline}
          {showAdvancedComponents && (
            <div>
              <AdvancedComponents
                formatMessage={formatMessage}
                series={data.stats}
                settings={moistureSensorSettings}
                show={showAdvancedComponents}
                tempConv={data.user.locale.tempConv}
                name={name}
              />
            </div>
          )}
        </div>
      )
    }

    return (
      <div>
        <Helmet><title>{title}</title></Helmet>
        <NavigationBar
          backFunc={back}
          page={page}
          name={name}
          logOut={() => logout(navigate)}
          rightControl={{
            icon: 'fa-cog',
            func: toAdvancedSettings,
          }}
        />
        <div className={pageStyles.pageWidth}>
          <div className={pageStyles.pageContainer}>
            <div className={pageStyles.dateRangePicker} style={{ position: 'relative' }}>
              <DateRangePicker
                dateBegin={dateBegin}
                dateEnd={dateEnd}
                dateType={dateType}
                dayPickerIsVisible={dayPickerIsVisible}
                draftRange={draftRange}
                onClose={closeDayPicker}
                onEditRange={editDateRange}
                onDayClick={handleDayClickWithStats}
                onApplyRange={applyDraftRange}
                onResetToLast31Days={resetToLast31Days}
                layout='inline'
                buttonStyle={{ marginLeft: '0px' }}
              />
              {dayPickerIsVisible && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  zIndex: 1000,
                  marginTop: '8px',
                  backgroundColor: 'white',
                  borderRadius: '12px',
                  padding: '20px',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                }}
                >
                  <DateRangePicker
                    dateBegin={dateBegin}
                    dateEnd={dateEnd}
                    dateType={dateType}
                    dayPickerIsVisible={dayPickerIsVisible}
                    draftRange={draftRange}
                    onClose={closeDayPicker}
                    onEditRange={editDateRange}
                    onDayClick={handleDayClickWithStats}
                    onApplyRange={applyDraftRange}
                    onResetToLast31Days={resetToLast31Days}
                    layout='modal'
                  />
                </div>
              )}
              <button
                type='button'
                onClick={toMoistureCombinedSettings}
                className={pageStyles.chartTitleSetting}
                style={{
                  cursor: 'pointer',
                  '&:focus': { outline: '#CCC' },
                  '&:hover': { background: '#CCC' },
                  position: 'absolute',
                  right: '20px',
                  top: '0px',
                }}
              >
                <i className='fa fa-sliders' />
              </button>
            </div>
            {pageContents}
          </div>
        </div>
        <Footer />
      </div>
    )
  }

  return render()
}

MoistureSensorPage.propTypes = {
  auth: PropTypes.object,
  data: PropTypes.object,
  pages: PropTypes.object,
  route: PropTypes.object,
  viewDays: PropTypes.number,
}

export default withAppStore(injectIntl(MoistureSensorPage))
